import { Component, OnInit, ViewChild, OnDestroy } from '@angular/core';
import { Router } from '@angular/router';
import { MatTableDataSource } from '@angular/material/table';
import { MatPaginator } from '@angular/material/paginator';
import { MatSort } from '@angular/material/sort';
import { MatSnackBar } from '@angular/material/snack-bar';
import { Subject, takeUntil } from 'rxjs';
import { ReportService, Report, ReportsListResponse } from '../services/report.service';
import { KundeService } from '../services/kunde.service';
import { StandortService } from '../services/standort.service';

export interface FilterOptions {
  client_id?: string;
  standort_id?: string;
  date_from?: string;
  date_to?: string;
  status?: string;
}

@Component({
  templateUrl: './reports.component.html',
  styleUrls: ['./reports.component.scss']
})
export class ReportsComponent implements OnInit, OnDestroy {
  displayedColumns: string[] = ['einsatznummer', 'client_name', 'standort_name', 'techniker_name', 'created_at', 'status', 'version', 'actions'];
  dataSource: MatTableDataSource<Report>;
  
  @ViewChild(MatPaginator) paginator: MatPaginator;
  @ViewChild(MatSort) sort: MatSort;

  private destroy$ = new Subject<void>();
  
  loading = false;
  totalItems = 0;
  currentPage = 1;
  pageSize = 20;
  searchTerm = '';
  
  // Filter options
  filters: FilterOptions = {};
  clients: any[] = [];
  standorte: any[] = [];
  
  // Status options
  statusOptions = [
    { value: '', label: 'Alle Status' },
    { value: 'generated', label: 'Generiert' },
    { value: 'sent', label: 'Versendet' }
  ];

  constructor(
    private snackBar: MatSnackBar,
    private router: Router,
    private reportService: ReportService,
    private kundeService: KundeService,
    private standortService: StandortService
  ) {
    this.dataSource = new MatTableDataSource<Report>([]);
  }

  ngOnInit(): void {
    console.log('✅ ReportsComponent ngOnInit - Component loaded successfully');
    console.log('📍 Current URL:', window.location.href);
    console.log('🎯 Component should be loaded within shell');
    this.loadFilterOptions();
    this.loadReports();
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  ngAfterViewInit() {
    this.dataSource.paginator = this.paginator;
    this.dataSource.sort = this.sort;
  }

  loadFilterOptions(): void {
    // Load clients
    this.kundeService.getKunden().pipe(
      takeUntil(this.destroy$)
    ).subscribe({
      next: (response) => {
        this.clients = response.data || [];
      },
      error: (error) => {
        console.error('Error loading clients:', error);
      }
    });

    // Load standorte
    this.standortService.getStandorte().pipe(
      takeUntil(this.destroy$)
    ).subscribe({
      next: (response) => {
        this.standorte = response.data || [];
      },
      error: (error) => {
        console.error('Error loading standorte:', error);
      }
    });
  }

  loadReports(): void {
    this.loading = true;
    
    const filters = {
      ...this.filters,
      page: this.currentPage,
      limit: this.pageSize
    };
    
    this.reportService.getReports(filters).pipe(
      takeUntil(this.destroy$)
    ).subscribe({
      next: (response: ReportsListResponse) => {
        this.dataSource.data = response.reports;
        this.totalItems = response.pagination.total;
        this.loading = false;
        console.log('✅ Reports loaded:', response.reports.length);
      },
      error: (error) => {
        this.loading = false;
        this.snackBar.open('Fehler beim Laden der Berichte', 'Schließen', { duration: 3000 });
        console.error('Error loading reports:', error);
      }
    });
  }

  onSearch(): void {
    // For now, just reload with current filters
    this.currentPage = 1;
    this.loadReports();
  }

  onFilterChange(): void {
    this.currentPage = 1;
    this.loadReports();
  }

  clearFilters(): void {
    this.filters = {};
    this.searchTerm = '';
    this.currentPage = 1;
    this.loadReports();
  }

  onPageChange(event: any): void {
    this.currentPage = event.pageIndex + 1;
    this.pageSize = event.pageSize;
    this.loadReports();
  }

  viewReport(report: Report): void {
    this.reportService.viewReportHtmlById(report.kontrolltermin_id);
  }

  downloadReport(report: Report): void {
    this.reportService.downloadReportById(report.uuid);
  }

  viewKontrolltermin(report: Report): void {
    // Navigate to kontrolltermin in abgeschlossen status since a report exists
    this.router.navigate(['/kontrolltermine', report.kontrolltermin_id], {
      queryParams: { 
        mode: 'view', 
        sourceStatus: 'abgeschlossen' 
      }
    });
  }

  getStatusClass(status: string): string {
    switch (status) {
      case 'sent':
        return 'bg-green-50 text-green-700 ring-green-600/20';
      case 'generated':
        return 'bg-blue-50 text-blue-700 ring-blue-600/20';
      default:
        return 'bg-gray-50 text-gray-700 ring-gray-600/20';
    }
  }

  getStatusText(status: string): string {
    switch (status) {
      case 'sent':
        return 'Versendet';
      case 'generated':
        return 'Generiert';
      default:
        return 'Unbekannt';
    }
  }

  formatFileSize(bytes: number): string {
    return this.reportService.formatFileSize(bytes);
  }

  formatDate(dateString: string): string {
    return this.reportService.formatDate(dateString);
  }
}
